import type { Category, DataPoint } from "fileParsing/types";
import { CompareArrows } from "@mui/icons-material";
import { Card, CardContent, CardHeader, Hidden, Paper, SvgIcon, Typography, useTheme } from "@mui/material";
import { Stack } from "@mui/system";
import { useDatapointsSelectionFiltered } from "containers/hooks/useData";
import useTranslations from "containers/hooks/useLanguageHook";
import React, { type FC, useCallback, useEffect } from "react";
import { type SubCategories, type VisualizationSettings, VisualizationSettingsDefaults } from ".";
import { Sensitivity, SensitivityLevel } from "./sensitivity";

type ComponentProps = {
  onSubcategoryChange?: (newCat: SubCategories) => void;
  settings: Partial<VisualizationSettings>;
};

const LEVELS = [SensitivityLevel.Low, SensitivityLevel.Medium, SensitivityLevel.High] as const;

type ItemValue = { category: Category; sensitivity: SensitivityLevel | null; count: number };

const DraggableElement = ({
  subcat,
  value,
  settings,
  onMove,
  label,
}: {
  subcat: string;
  value: ItemValue;
  settings: VisualizationSettings;
  onMove: (subcat: string, direction: -1 | 1) => void;
  label: string;
}) => {
  const theme = useTheme();

  return (
    // ponytail: native HTML5 DnD; mouse via draggable, keyboard via arrow keys
    <Paper
      draggable
      tabIndex={0}
      role="option"
      aria-label={label}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", subcat);
        e.dataTransfer.effectAllowed = "move";
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") onMove(subcat, 1);
        else if (e.key === "ArrowLeft") onMove(subcat, -1);
        else return;
        e.preventDefault();
      }}
      style={{
        padding: "4px",
        marginBottom: "10px",
        cursor: "grab",
        border: `${settings.borderWidth}px solid ${theme.palette.categories[value.category].main}`,
        backgroundColor: theme.palette.categories[value.category].main,
        color: "black",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <Typography>{`${subcat} (${value.count})`}</Typography>
    </Paper>
  );
};

const DragDropContextPieChart: FC<ComponentProps> = (props: ComponentProps) => {
  const { t } = useTranslations();
  const dataSelected = useDatapointsSelectionFiltered();

  const [subCategories, setSubCategories] = React.useState<SubCategories>({});
  const [dragOver, setDragOver] = React.useState<SensitivityLevel | null>(null);
  const listData = Object.entries(subCategories).sort((l, r) => l[1].category.localeCompare(r[1].category));

  const getSettings = useCallback((): VisualizationSettings => {
    return { ...VisualizationSettingsDefaults, ...props.settings };
  }, [props.settings]);

  const settings = getSettings();

  const getSensitivityString = useCallback(
    (level: SensitivityLevel) => {
      switch (level) {
        case SensitivityLevel.Low:
          return t.Vis.SensitivityLow;
        case SensitivityLevel.Medium:
          return t.Vis.SensitivityMedium;
        case SensitivityLevel.High:
          return t.Vis.SensitivityHigh;
      }
    },
    [t.Vis],
  );

  const getSubCategories = useCallback(() => {
    const temp = dataSelected.reduce<SubCategories>((acc, d: DataPoint) => {
      let subcat = d.subcategory;

      const match = settings.subcategoryAggregation.find((s) => d.subcategory.startsWith(s));
      if (match !== undefined) subcat = `${match} ...`;

      return {
        ...acc,
        [subcat]: {
          category: d.category,
          sensitivity: subCategories?.[subcat] ? subCategories[subcat].sensitivity : null,
          count: acc[subcat] ? acc[subcat].count + 1 : 1,
        },
      };
    }, {} as SubCategories);

    const sensitivities = Sensitivity.getSensitivity(temp);

    Object.entries(sensitivities).forEach(([subcat, sensitivity]) => {
      if (!temp[subcat].sensitivity) {
        temp[subcat].sensitivity = sensitivity;
      }
    });

    return temp;
  }, [dataSelected.reduce, settings.subcategoryAggregation.find, subCategories]);

  const assign = useCallback((subcat: string, level: SensitivityLevel) => {
    setSubCategories((prev) =>
      subcat in prev && prev[subcat].sensitivity !== level
        ? { ...prev, [subcat]: { ...prev[subcat], sensitivity: level } }
        : prev,
    );
  }, []);

  const moveRelative = useCallback(
    (subcat: string, direction: -1 | 1) => {
      const current = subCategories[subcat]?.sensitivity;
      const from = LEVELS.indexOf(current as (typeof LEVELS)[number]);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= LEVELS.length) return;
      assign(subcat, LEVELS[to]);
    },
    [assign, subCategories],
  );

  function cardHeader(level: SensitivityLevel) {
    return (
      <CardHeader
        style={{ color: settings.colors[level], textAlign: "center", paddingTop: "0.8em", paddingBottom: 0 }}
        title={
          <span>
            <SvgIcon style={{ position: "relative", top: 4, marginRight: 4 }}>
              <path d={settings.icons[level]} />
            </SvgIcon>
            {getSensitivityString(level)}
          </span>
        }
        subheader={`${Object.values(subCategories).reduce(
          (sum, sc) => (sc.sensitivity === level ? sum + sc.count : sum),
          0,
        )} ${t.DataPage.DataPoints}`}
      />
    );
  }

  function column(level: SensitivityLevel) {
    return (
      <Card
        key={level}
        sx={{
          width: "100%",
          minHeight: "20rem",
          overflowY: "auto",
          border: `4px solid ${settings.colors[level]}`,
          outline: dragOver === level ? `2px dashed ${settings.colors[level]}` : "none",
        }}
      >
        {cardHeader(level)}
        <CardContent
          role="listbox"
          aria-label={getSensitivityString(level)}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setDragOver(level);
          }}
          onDragLeave={() => setDragOver((prev) => (prev === level ? null : prev))}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(null);
            const subcat = e.dataTransfer.getData("text/plain");
            if (subcat) assign(subcat, level);
          }}
          sx={{ minHeight: "6rem" }}
        >
          {listData
            .filter(([, v]) => v.sensitivity === level)
            .map(([subcat, value]) => (
              <DraggableElement
                key={subcat}
                subcat={subcat}
                value={value}
                settings={settings}
                onMove={moveRelative}
                label={`${subcat}, ${getSensitivityString(level)}`}
              />
            ))}
        </CardContent>
      </Card>
    );
  }

  useEffect(() => {
    if (props.onSubcategoryChange) props.onSubcategoryChange(subCategories);
  }, [subCategories, props.onSubcategoryChange]);

  useEffect(() => {
    setSubCategories(getSubCategories);
  }, [getSubCategories]);

  return (
    <Stack direction="column" sx={{ height: { xs: "100%", md: props.settings.height } }}>
      <Typography align="center" pb={1}>
        {t.Vis.PieChartChangeCategorySensitivity}
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-evenly"
        spacing="2%"
        divider={
          <Hidden mdDown>
            <CompareArrows style={{ width: 32, height: 32, right: 4, top: 20 }} />
          </Hidden>
        }
        sx={{ paddingX: "2%", flexShrink: 1, minHeight: { md: 0 } }}
      >
        {Array.from(LEVELS).map(column)}
      </Stack>
    </Stack>
  );
};

export default DragDropContextPieChart;
